import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ApprovalService } from '../../services/approval.service';
import { ApprovalRequestDetail } from '../../interfaces/approval.interface';
import { formatDate } from '../../utils/dateUtils';

const COLOR: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'error', CANCELLED: 'default', WAITING: 'warning', SKIPPED: 'default',
};

interface Props {
  subjectType: string;
  subjectId: string;
  /** Changes whenever the document's own approval state changed, so the card refetches. */
  refreshKey?: string | null;
}

/** The approval state of one document: workflow, who decided what, who is still to decide. Renders nothing when never submitted. */
const ApprovalStatusCard: React.FC<Props> = ({ subjectType, subjectId, refreshKey }) => {
  const [detail, setDetail] = useState<ApprovalRequestDetail | null>(null);

  useEffect(() => {
    let live = true;
    Promise.resolve(ApprovalService.forSubject(subjectType, subjectId))
      .then(r => { if (live) setDetail(r?.request ?? null); })
      .catch(() => { if (live) setDetail(null); });
    return () => { live = false; };
  }, [subjectType, subjectId, refreshKey]);

  if (!detail) return null;
  const r = detail.request;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="approval-status-card">
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle1">Approval</Typography>
        <Chip size="small" color={COLOR[r.status]} label={r.status} />
        {r.workflowName && <Typography variant="caption" color="text.secondary">{r.workflowName}</Typography>}
        <Typography variant="caption" color="text.secondary">
          submitted by {r.submittedByName ?? '—'} on {formatDate(r.submittedAt)}
        </Typography>
        <RouterLink to={`/approvals?request=${r.id}`}>Open in inbox</RouterLink>
      </Box>
      {detail.steps.map(s => (
        <Box key={s.seq} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="body2">{s.seq}. {s.approverLabel}</Typography>
          <Chip size="small" color={COLOR[s.status]} label={s.status} />
          {s.decidedByName && <Typography variant="caption">{s.decidedByName}{s.decidedAt ? `, ${formatDate(s.decidedAt)}` : ''}</Typography>}
          {s.comment && <Typography variant="caption" color="text.secondary">“{s.comment}”</Typography>}
        </Box>
      ))}
      {r.status === 'REJECTED' && <Alert severity="error" sx={{ mt: 1, py: 0 }}>Rejected: edit the document and submit it again.</Alert>}
    </Paper>
  );
};

export default ApprovalStatusCard;
